import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import { Button, DataRecordCard } from '@/shared/ui';
import { deleteIntegration, selectIsLoading } from '../../model/slice';
import type { Datasource, RedditReportingIntegration } from '../../model/types';
import { useDatasourceIntegrations } from '../../model/useDatasourceIntegrations';
import { useURLSearchParamsBroadcaster } from '../../model/useURLSearchParamsBroadcaster';
import { DatasourcePanel } from '../DatasourcePanel/DatasourcePanel';
import { IntegrationSyncInfo } from '../IntegrationSyncInfo/IntegrationSyncInfo';
import { BROADCAST_CHANNEL_NAME } from './model/constants';
import { useRedditCode } from './model/useRedditCode';
import { RedditIcon } from './ui/RedditIcon/RedditIcon';

const RedditButton = styled(Button)(() => ({
  borderRadius: 24,
}));

interface Props {
  datasource: Datasource;
}

export function RedditDatasource({ datasource }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const integrationsLoading = useAppSelector(selectIsLoading);
  const datasourceIntegrations = useDatasourceIntegrations(
    datasource,
  ) as RedditReportingIntegration[];
  const { processing, openDataAccessDialog } = useRedditCode();

  useURLSearchParamsBroadcaster({
    channelName: BROADCAST_CHANNEL_NAME,
    params: ['code', 'state'],
  });

  const handleDeleteIntegration = (id: string) => () => {
    return dispatch(deleteIntegration(id))
      .unwrap()
      .then(() => {
        enqueueSnackbar('Integration successfully deleted.', {
          variant: 'success',
          autoHideDuration: 2000,
        });
      })
      .catch(() => {
        enqueueSnackbar('Unable to delete integration. Please, try again.', {
          variant: 'error',
          autoHideDuration: 4000,
        });
      });
  };

  return (
    <DatasourcePanel
      loading={integrationsLoading || processing}
      datasource={datasource}
      addButtonSlot={
        <RedditButton
          variant="outlined"
          color="secondary"
          startIcon={<RedditIcon />}
          onClick={openDataAccessDialog}
        >
          Add account
        </RedditButton>
      }
      integrationsSlot={datasourceIntegrations.map((integration) => {
        return (
          <DataRecordCard
            key={integration.id}
            recordNoun="integration"
            title={integration.integrationConfig.username}
            titleExtra={
              <IntegrationSyncInfo
                status={integration.status}
                lastSuccessfulSync={integration.lastSuccessfulSync}
              />
            }
            onDelete={handleDeleteIntegration(integration.id)}
          />
        );
      })}
    />
  );
}
